<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";

interface Props {
  isOpen: boolean;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  size: "md",
});

const emit = defineEmits<{
  (e: "close"): void;
}>();

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};
</script>

<template>
  <TransitionRoot :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50">
      <TransitionChild
        as="template"
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-200"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-150"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                'w-full transform overflow-hidden rounded-xl bg-white shadow-xl transition-all',
                sizeClasses[size],
              ]"
            >
              <div
                class="flex items-center justify-between border-b border-gray-200 p-4"
              >
                <DialogTitle
                  as="h3"
                  class="text-lg font-semibold text-gray-900"
                >
                  {{ title }}
                </DialogTitle>
                <button
                  @click="emit('close')"
                  class="rounded-lg p-1 transition-colors hover:bg-gray-100"
                >
                  <XMarkIcon class="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div class="p-4">
                <slot></slot>
              </div>

              <div
                v-if="$slots.footer"
                class="flex items-center justify-end gap-3 border-t border-gray-200 p-4"
              >
                <slot name="footer"></slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
